import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { useTranslation } from "react-i18next";

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideBack = keyframes`
  0%, 12% { opacity: 0.35; transform: translateX(18px) scale(0.92); }
  40%, 62% { opacity: 1; transform: translateX(0) scale(1); }
  100% { opacity: 0.55; transform: translateX(-6px) scale(0.96); }
`;

const fadeClient = keyframes`
  0%, 25% { opacity: 1; transform: scale(1); }
  70% { opacity: 0.35; transform: scale(0.88) translateX(-8px); }
  100% { opacity: 0.12; transform: scale(0.8) translateX(-16px); }
`;

const bob = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
`;

const LEAVE_ACCENT = "#6a1b9a";

function UnleavePreviewStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.35,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "14%",
          top: 16,
          width: 48,
          height: 56,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.16)}`,
          animation: `${fadeClient} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <StorefrontOutlinedIcon sx={{ fontSize: 24, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "44%",
          top: 30,
          width: 34,
          height: 34,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.14),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 1.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <UndoOutlinedIcon sx={{ fontSize: 18, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "14%",
          top: 16,
          width: 52,
          height: 56,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.12),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.2)}`,
          animation: `${slideBack} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <HomeOutlinedIcon sx={{ fontSize: 26, color: accent }} />
      </Box>
    </Box>
  );
}

/** Confirma deshacer SE QUEDA EN CLIENTE, con motion. */
export default function MeetingUnleaveSamplesDialog({
  open,
  onClose,
  onConfirm,
  busy = false,
  sampleName = "",
  count = 1,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = LEAVE_ACCENT;
  const n = Math.max(1, Number(count) || 1);
  const name = String(sampleName || "").trim();

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      className="meeting-no-print"
      // Por encima del diálogo de escaneo (ese usa modal+2).
      // No poner zIndex en el backdrop: si no, el blur tapa el paper.
      sx={{ zIndex: (th) => th.zIndex.modal + 20 }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(10px)",
            backgroundColor: "rgba(15, 23, 42, 0.45)",
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.16)} 0%, ${alpha(
            theme.palette.primary.main,
            0.05,
          )} 100%)`,
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ minWidth: 0 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(accent, 0.16),
                color: accent,
                flexShrink: 0,
              }}
            >
              <UndoOutlinedIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, lineHeight: 1.15 }}
              >
                {t("Meetings.leaveSamplesUndoTitle")}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", lineHeight: 1.4, mt: 0.3 }}
              >
                {t("Meetings.leaveSamplesUndoHint")}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label={t("Meetings.cancel")}
            onClick={onClose}
            disabled={busy}
            size="small"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
        <UnleavePreviewStage accent={accent} />
      </Box>

      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
            fontWeight: 600,
            animation: `${rise} 300ms ease both`,
            ...motionSafe,
          }}
        >
          {name
            ? t("Meetings.leaveSamplesUndoBodyNamed", { name, count: n })
            : t("Meetings.leaveSamplesUndoBody", { count: n })}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1.25}
        justifyContent="flex-end"
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.background.default, 0.65),
        }}
      >
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ fontWeight: 700, minHeight: 44 }}
        >
          {t("Meetings.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy}
          onClick={onConfirm}
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <UndoOutlinedIcon />
            )
          }
          sx={{
            minHeight: 48,
            fontWeight: 800,
            borderRadius: 2,
            bgcolor: accent,
            boxShadow: `0 8px 18px ${alpha(accent, 0.32)}`,
            "&:hover": { bgcolor: "#4a148c" },
          }}
        >
          {t("Meetings.leaveSamplesUndoConfirm")}
        </Button>
      </Stack>
    </Dialog>
  );
}
